import { useState, useEffect, useRef, useCallback } from 'react';
import { resolveApiUrl, resolveWsUrl } from '@/providers/api-url';
import { getActiveTenant, getPreferredTenant } from '@/lib/active-tenant';

interface TypingUser {
  type: 'user' | 'admin';
  id: number;
  firstName: string | null;
  username: string | null;
}

interface WsMessageSender {
  type: 'user' | 'admin';
  id: number;
  firstName: string | null;
  lastName: string | null;
  username: string | null;
}

interface WsMessageReaction {
  emoji: string;
  count: number;
  hasReacted: boolean;
}

interface WsMessageReadBy {
  type: 'user' | 'admin';
  id: number;
  firstName: string | null;
  username: string | null;
  readAt: string;
}

export interface WsMessageAttachment {
  id: number;
  fileId: number;
  type: 'image' | 'video' | 'audio' | 'document';
  url: string;
  name: string;
  size: number;
  mimeType: string;
}

export interface WsNewMessage {
  id: number;
  chatRoomId: number;
  content: string;
  isSystemMessage: boolean;
  replyToMessageId: number | null;
  status: 'SENT' | 'DELIVERED' | 'READ';
  isEdited: boolean;
  editedAt: string | null;
  isDeleted: boolean;
  createdAt: string;
  sender: WsMessageSender | null;
  replyTo: null;
  reactions: WsMessageReaction[];
  readBy: WsMessageReadBy[];
  attachments?: WsMessageAttachment[];
}

type WsServerMessage =
  | { type: 'connected'; userId: number; isAdmin: boolean }
  | { type: 'subscribed'; roomId: number }
  | { type: 'new_message'; message: WsNewMessage }
  | { type: 'message_edited'; messageId: number; roomId: number; content: string; editedAt: string }
  | { type: 'message_deleted'; messageId: number; roomId: number }
  | { type: 'user_typing'; roomId: number; user: TypingUser }
  | { type: 'user_stopped_typing'; roomId: number; userId: number; userType: 'user' | 'admin' }
  | { type: 'user_online'; user: { type: 'user' | 'admin'; id: number } }
  | { type: 'user_offline'; userId: number; userType: 'user' | 'admin'; lastSeenAt: string }
  | { type: 'message_read'; messageId: number; roomId: number; reader: TypingUser; readAt: string }
  | { type: 'ping' }
  | { type: 'error'; message: string; code?: string }
  | { type: 'room_update'; roomId: number; lastMessage: WsRoomUpdateData['lastMessage']; lastMessageAt: string };

export interface WsRoomUpdateData {
  roomId: number;
  lastMessage: {
    id: number;
    content: string;
    createdAt: string;
    sender: { type: 'user' | 'admin'; id: number; firstName: string | null; lastName: string | null; username: string | null } | null;
  };
  lastMessageAt: string;
}

interface UseAdminChatWsOptions {
  onNewMessage?: (message: WsNewMessage) => void;
  onMessageEdited?: (data: { messageId: number; roomId: number; content: string; editedAt: string }) => void;
  onMessageDeleted?: (data: { messageId: number; roomId: number }) => void;
  onTyping?: (data: { roomId: number; user: TypingUser }) => void;
  onStoppedTyping?: (data: { roomId: number; userId: number; userType: 'user' | 'admin' }) => void;
  onMessageRead?: (data: { messageId: number; roomId: number; reader: TypingUser; readAt: string }) => void;
  onRoomUpdate?: (data: WsRoomUpdateData) => void;
  enabled?: boolean;
}

interface UseAdminChatWsReturn {
  isConnected: boolean;
  connectionId: number;
  subscribe: (roomId: number) => void;
  unsubscribe: (roomId: number) => void;
  sendMessage: (
    roomId: number,
    content: string,
    replyToMessageId?: number,
    attachmentFileIds?: number[],
  ) => void;
  editMessage: (messageId: number, content: string) => void;
  deleteMessage: (messageId: number) => void;
  startTyping: (roomId: number) => void;
  stopTyping: (roomId: number) => void;
  markRead: (roomId: number, messageId: number) => void;
}

const MAX_RETRIES = 5;
const BASE_DELAY = 1000;
const MAX_DELAY = 30000;

type WsSendable = string | number | boolean | number[] | undefined;

const extractTokenExp = (token: string): number | null => {
  const parts = token.split('.');
  if (parts.length < 2) { return null; }
  try {
    const decoded = atob(parts[1]);
    const match = decoded.match(/"exp"\s*:\s*(\d+)/);
    if (!match) { return null; }
    const exp = Number(match[1]);
    return Number.isFinite(exp) ? exp : null;
  } catch {
    return null;
  }
};

const isTokenExpired = (token: string): boolean => {
  const exp = extractTokenExp(token);
  if (!exp) { return false; }
  return Date.now() >= exp * 1000;
};

const refreshAccessToken = async (): Promise<string | null> => {
  try {
    const tenant = getActiveTenant() ?? getPreferredTenant();
    const response = await fetch(`${resolveApiUrl()}/auth/admin/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: tenant ? { 'X-Tenant-Id': tenant } : undefined,
    });

    if (!response.ok) {
      localStorage.removeItem('accessToken');
      return null;
    }

    const payload = (await response.json()) as {
      success: boolean;
      data: { accessToken: string } | null;
    };

    if (!payload.success || !payload.data?.accessToken) {
      localStorage.removeItem('accessToken');
      return null;
    }

    localStorage.setItem('accessToken', payload.data.accessToken);
    return payload.data.accessToken;
  } catch {
    localStorage.removeItem('accessToken');
    return null;
  }
};

const getValidToken = async (): Promise<string | null> => {
  let token = localStorage.getItem('accessToken');
  if (!token) { return null; }

  if (isTokenExpired(token)) {
    token = await refreshAccessToken();
  }

  return token;
};

export function useAdminChatWs(options: UseAdminChatWsOptions = {}): UseAdminChatWsReturn {
  const {
    onNewMessage,
    onMessageEdited,
    onMessageDeleted,
    onTyping,
    onStoppedTyping,
    onMessageRead,
    onRoomUpdate,
    enabled = true,
  } = options;

  const [isConnected, setIsConnected] = useState(false);
  const [connectionId, setConnectionId] = useState(0);
  const wsRef = useRef<WebSocket | null>(null);
  const retryCountRef = useRef(0);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);

  // Храним callbacks в ref, чтобы не пересоздавать connect при смене callbacks
  const callbacksRef = useRef(options);
  callbacksRef.current = {
    onNewMessage,
    onMessageEdited,
    onMessageDeleted,
    onTyping,
    onStoppedTyping,
    onMessageRead,
    onRoomUpdate,
    enabled,
  };

  const sendWsMessage = useCallback((data: Record<string, WsSendable>) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(data));
    }
  }, []);

  const connect = useCallback(async () => {
    if (!mountedRef.current) { return; }

    const token = await getValidToken();
    if (!token) { return; }

    const wsUrl = `${resolveWsUrl()}/ws/chat?token=${token}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      if (!mountedRef.current) {
        ws.close();
        return;
      }
      setIsConnected(true);
      setConnectionId((prev) => prev + 1);
      retryCountRef.current = 0;
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data as string) as WsServerMessage;
        const cb = callbacksRef.current;

        switch (data.type) {
          case 'ping':
            ws.send(JSON.stringify({ type: 'pong' }));
            break;

          case 'new_message':
            cb.onNewMessage?.(data.message);
            break;

          case 'message_edited':
            cb.onMessageEdited?.({
              messageId: data.messageId,
              roomId: data.roomId,
              content: data.content,
              editedAt: data.editedAt,
            });
            break;

          case 'message_deleted':
            cb.onMessageDeleted?.({
              messageId: data.messageId,
              roomId: data.roomId,
            });
            break;

          case 'user_typing':
            cb.onTyping?.({
              roomId: data.roomId,
              user: data.user,
            });
            break;

          case 'user_stopped_typing':
            cb.onStoppedTyping?.({
              roomId: data.roomId,
              userId: data.userId,
              userType: data.userType,
            });
            break;

          case 'message_read':
            cb.onMessageRead?.({
              messageId: data.messageId,
              roomId: data.roomId,
              reader: data.reader,
              readAt: data.readAt,
            });
            break;

          case 'room_update':
            cb.onRoomUpdate?.({
              roomId: data.roomId,
              lastMessage: data.lastMessage,
              lastMessageAt: data.lastMessageAt,
            });
            break;

          case 'error':
            break;

          // connected, subscribed, user_online, user_offline — не требуют обработки
          default:
            break;
        }
      } catch {
        // Невалидное WS-сообщение — игнорируем
      }
    };

    ws.onclose = (event) => {
      if (!mountedRef.current) { return; }

      setIsConnected(false);
      wsRef.current = null;

      // Попытка refresh при 4003 (token expired)
      if (event.code === 4003) {
        void (async () => {
          const refreshed = await refreshAccessToken();
          if (refreshed && mountedRef.current) {
            retryCountRef.current = 0;
            void connect();
          }
        })();
        return;
      }

      // Не переподключаемся при нормальном закрытии или auth ошибках
      if (
        event.code === 1000 ||
        event.code === 1005 ||
        event.code === 4001 ||
        event.code === 4002
      ) {
        return;
      }

      // Exponential backoff reconnect
      if (retryCountRef.current < MAX_RETRIES) {
        const delay = Math.min(BASE_DELAY * 2 ** retryCountRef.current, MAX_DELAY);
        retryCountRef.current += 1;
        reconnectTimeoutRef.current = setTimeout(() => {
          if (mountedRef.current) {
            void connect();
          }
        }, delay);
      }
    };

    ws.onerror = () => {
      ws.close();
    };
  }, []);

  useEffect(() => {
    mountedRef.current = true;

    if (enabled) {
      void connect();
    }

    return () => {
      mountedRef.current = false;

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      if (wsRef.current) {
        wsRef.current.close(1000);
        wsRef.current = null;
      }
    };
  }, [enabled, connect]);

  const subscribe = useCallback((roomId: number) => {
    sendWsMessage({ type: 'subscribe_room', roomId });
  }, [sendWsMessage]);

  const unsubscribe = useCallback((roomId: number) => {
    sendWsMessage({ type: 'unsubscribe_room', roomId });
  }, [sendWsMessage]);

  const sendMessage = useCallback((
    roomId: number,
    content: string,
    replyToMessageId?: number,
    attachmentFileIds?: number[],
  ) => {
    const msg: Record<string, WsSendable> = {
      type: 'send_message',
      roomId,
      content,
    };
    if (replyToMessageId !== undefined) {
      msg.replyToMessageId = replyToMessageId;
    }
    if (attachmentFileIds !== undefined && attachmentFileIds.length > 0) {
      msg.attachmentFileIds = attachmentFileIds;
    }
    sendWsMessage(msg);
  }, [sendWsMessage]);

  const editMessage = useCallback((messageId: number, content: string) => {
    sendWsMessage({ type: 'edit_message', messageId, content });
  }, [sendWsMessage]);

  const deleteMessage = useCallback((messageId: number) => {
    sendWsMessage({ type: 'delete_message', messageId });
  }, [sendWsMessage]);

  const startTyping = useCallback((roomId: number) => {
    sendWsMessage({ type: 'typing_start', roomId });
  }, [sendWsMessage]);

  const stopTyping = useCallback((roomId: number) => {
    sendWsMessage({ type: 'typing_stop', roomId });
  }, [sendWsMessage]);

  const markRead = useCallback((roomId: number, messageId: number) => {
    sendWsMessage({ type: 'mark_read', roomId, messageId });
  }, [sendWsMessage]);

  return {
    isConnected,
    connectionId,
    subscribe,
    unsubscribe,
    sendMessage,
    editMessage,
    deleteMessage,
    startTyping,
    stopTyping,
    markRead,
  };
}
